import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  CreateShipmentRequest,
  Shipment,
  ShipmentExportCriteria,
  ShipmentPagination,
  ShipmentSearchCriteria
} from '../models';
import { MetricAction, MetricActionType, MetricModule, MetricType, Metrics } from '../models/metrics';
import { BaseService } from './base.service';

@Injectable()
export class ShipmentService extends BaseService {
  public headers: HttpHeaders;

  constructor(private readonly http: HttpClient) {
    super();
    this.envService = this.env.services.shipment;
    this.headers = new HttpHeaders(this.envService.headers);
  }

  public createShipment(request: CreateShipmentRequest): Observable<Shipment> {
    return this.http.post<Shipment>(this.getUrl(), request, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public searchShipment(criteria: ShipmentSearchCriteria): Observable<ShipmentPagination> {
    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.SHIPMENT,
          route: this.envService.url,
          action: MetricActionType.SEARCH
        }
      ]
    };
    const params = this.getParams(criteria, true);

    return this.http.get<ShipmentPagination>(this.getUrl(), {
      headers: this.loaderHeaders(null, metricsReq),
      observe: 'body',
      params
    });
  }

  public updateShipment(request: Shipment): Observable<Shipment> {
    return this.http.put<Shipment>(this.getUrl(), request, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public getShipmentByShipmentNumber(shipmentNo: string): Observable<Shipment> {
    const url = this.getFullUrl(this.envService.getByShipmentNumber, {
      shipmentNo
    });

    return this.http.get<Shipment>(url, {
      headers: this.headers,
      observe: 'body'
    });
  }

  public deleteByShipmentNumber(shipmentNo: string): Observable<any> {
    const url = this.getFullUrl(this.envService.deleteByShipmentNumber, { shipmentNo });

    return this.http.delete<any>(url, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public confirmLoad(request: Shipment): Observable<Shipment> {
    const url = this.getFullUrl(this.envService.confirmLoad);

    return this.http.post<Shipment>(url, request, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public dispatchShipmentByShipmentNumber(shipmentNo: string): Observable<Shipment> {
    const url = this.getFullUrl(this.envService.dispatch, {
      shipmentNo
    });

    return this.http.post<Shipment>(url, {}, { headers: this.loaderHeaders(2000) });
  }

  public exportShipment(criteria: ShipmentExportCriteria): Observable<any> {
    const params = this.getParams(criteria, true);
    const url = this.getFullUrl(this.envService.exportShipment);

    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.COUNTER,
          module: MetricModule.SHIPMENT,
          path: `${this.envService.url}${this.envService.exportShipment}`,
          action: MetricActionType.EXPORT
        },
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.SHIPMENT,
          route: `${this.envService.url}${this.envService.exportShipment}`,
          action: MetricActionType.EXPORT
        }
      ]
    };

    return this.http.get<any>(url, {
      headers: this.loaderHeaders(0, metricsReq),
      observe: 'body',
      params,
      responseType: 'blob' as 'json'
    });
  }

  public downloadFileTemplate(): Observable<any> {
    const url = this.getFullUrl(this.envService.downloadFileTemplate);
    return this.http.get<any>(url, { headers: this.headers, responseType: 'blob' as 'json' });
  }

  public importFile(file: File): Observable<any> {
    const url = this.getFullUrl(this.envService.importValidate);
    let formData = null;
    formData = new FormData();
    formData.append('file', file, file.name);

    this.headers.append('Content-Type', 'multipart/form-data');

    return this.http.post<any>(url, formData, {
      headers: this.loaderHeaders(),
      observe: 'body'
    });
  }

  public submitImport(data: any): Observable<any> {
    const url = this.getFullUrl(this.envService.submitImport);
    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.COUNTER,
          module: MetricModule.SHIPMENT,
          path: `${this.envService.importSubmit}`
        },
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.SHIPMENT,
          route: `${this.envService.importSubmit}`,
          action: MetricAction.IMPORT
        }
      ]
    };
    return this.http.post<any>(url, data, {
      headers: this.loaderHeaders(0, metricsReq),
      observe: 'body'
    });
  }

  public printCustomerDeliveryNote(shipmentNo: string): Observable<any> {
    const url = this.getFullUrl(this.envService.print, { shipmentNo });

    return this.http.get<any>(url, {
      headers: this.loaderHeaders(),
      observe: 'body',
      responseType: 'blob' as 'json'
    });
  }
}
